import type { PlaybackStats } from "@limrun/ui";
import { useEffect, useState } from "react";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";

function number(value: number | undefined, unit = "", digits = 0) {
  return value === undefined || !Number.isFinite(value) ? "—" : `${value.toFixed(digits)}${unit}`;
}

function Metric({ label, value }: { label: string; value: string }) {
  return <div className="space-y-1">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="font-medium tabular-nums">{value}</dd>
  </div>;
}

export const PLAYBACK_HISTORY_MS = 30_000;

export function appendPlaybackHistory(history: PlaybackStats[], sample: PlaybackStats) {
  // Browser timestamps can restart with a new peer connection.
  const previous = history[history.length - 1];
  const recent = previous && sample.timestamp < previous.timestamp ? [] : history;
  return [...recent.filter(item => item.timestamp > sample.timestamp - PLAYBACK_HISTORY_MS && item.timestamp < sample.timestamp), sample].slice(-60);
}

export function bufferHistoryPoints(history: PlaybackStats[], id: string) {
  const latest = history[history.length - 1]?.timestamp ?? 0;
  return history.flatMap((sample, index) => {
    const value = sample.video.find(video => video.id === id)?.bufferDelayMs;
    const point = { seconds: (sample.timestamp - latest) / 1000, delay: value !== undefined && Number.isFinite(value) ? value : null };
    const previous = history[index - 1];
    // Pausing collection must not draw a continuous line across unobserved playback.
    return previous && sample.timestamp - previous.timestamp > 5_000
      ? [{ seconds: (previous.timestamp - latest) / 1000 + 0.001, delay: null }, point]
      : [point];
  });
}

function BufferHistory({ history, video }: { history: PlaybackStats[]; video: PlaybackStats["video"][number] }) {
  const data = bufferHistoryPoints(history, video.id);
  const display = video.display === "inner" ? "Inner display" : "Primary display";
  return <div className="space-y-2">
    <div className="flex justify-between text-xs text-muted-foreground"><span>Buffer delay</span><span>Last 30 seconds</span></div>
    <ChartContainer config={{ delay: { label: "Buffer delay", color: "var(--color-brand)" } }}
      role="img" aria-label={`${display} buffer delay over the last 30 seconds`}
      className="aspect-auto h-28 w-full">
      <LineChart data={data} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="seconds" type="number" domain={[-30, 0]} ticks={[-30, -15, 0]}
          tickFormatter={seconds => seconds === 0 ? "now" : `${seconds}s`} axisLine={false} tickLine={false} />
        <YAxis width={48} domain={[0, "auto"]} tickCount={3} tickFormatter={value => `${value} ms`} axisLine={false} tickLine={false} />
        <ChartTooltip content={<ChartTooltipContent labelFormatter={(_, payload) => {
          // The shared tooltip passes the series label, so read time from the hovered sample.
          const seconds = payload[0]?.payload?.seconds;
          if (typeof seconds !== "number" || !Number.isFinite(seconds)) return null;
          return seconds === 0 ? "now" : `${Math.abs(seconds).toFixed(0)}s ago`;
        }}
          formatter={value => <span className="tabular-nums">{Number(value).toFixed(1)} ms</span>} />} />
        <Line type="linear" dataKey="delay" stroke="var(--color-delay)" strokeWidth={2}
          dot={{ r: 2, strokeWidth: 0, fill: "var(--color-delay)" }} connectNulls={false} isAnimationActive={false} />
      </LineChart>
    </ChartContainer>
  </div>;
}

export function PlaybackStatsPanel({ stats }: { stats: PlaybackStats | null }) {
  const [history, setHistory] = useState<PlaybackStats[]>([]);
  useEffect(() => { if (stats) setHistory(previous => appendPlaybackHistory(previous, stats)); }, [stats]);
  return <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-sm font-semibold">Playback stats</h2>
      <span className="text-xs text-muted-foreground">{stats ? "Updates every 2s" : "Waiting for stream"}</span>
    </div>
    {!stats ? <p className="text-sm text-muted-foreground">Measurements appear while playback is connected and this tab is visible.</p> : <>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <Metric label="Requested target" value={stats.requestedTargetMs === null ? "Automatic" : number(stats.requestedTargetMs, " ms")} />
        <Metric label="Network RTT" value={number(stats.rttMs, " ms")} />
      </dl>
      {stats.video.map(video => <section key={video.id} aria-label={video.display === "inner" ? "Inner display" : "Primary display"} className="space-y-3 border-t pt-3">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <h3>{stats.video.length > 1 ? video.display === "inner" ? "Inner display" : "Primary display" : "Video"}</h3>
          <span>{video.intervalMs === undefined ? "Collecting sample…" : `Last ${(video.intervalMs / 1000).toFixed(1)}s`}</span>
        </div>
        <BufferHistory history={history} video={video} />
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <Metric label="Buffer delay" value={number(video.bufferDelayMs, " ms", 1)} />
          <Metric label="Decoded FPS" value={number(video.fps, "", 1)} />
          <Metric label="Dropped frames" value={number(video.droppedFrames)} />
          <Metric label="Completed freezes" value={number(video.freezes)} />
          <Metric label="Received bitrate" value={number(video.bitrateBps === undefined ? undefined : video.bitrateBps / 1_000_000, " Mbps", 2)} />
        </dl>
      </section>)}
      {stats.audio.map(audio => <dl key={audio.id} aria-label="Audio" className="grid grid-cols-2 gap-3 border-t pt-3 text-sm">
        <Metric label="Audio buffer delay" value={number(audio.bufferDelayMs, " ms", 1)} />
        <Metric label="Audio bitrate" value={number(audio.bitrateBps === undefined ? undefined : audio.bitrateBps / 1000, " kbps", 1)} />
      </dl>)}
    </>}
    <p className="border-t pt-3 text-xs text-muted-foreground">Buffer delay is not tap-to-screen latency. Static screens can have low FPS. Unavailable measurements show —.</p>
  </div>;
}
